import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Logo from '../components/Logo';
import SEO from '../components/SEO';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO title="Cookie Policy — id100" description="How id100 uses browser storage and cookies. We do not use third-party trackers." path="/cookies" />
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-5 w-5" />Back
          </Link>
          <Logo size="sm" />
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold mb-6">Cookie Policy</h1>
          <p className="text-gray-500 mb-6">Last updated: January 2026</p>
          
          <div className="prose prose-sm max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. What Are Cookies?</h2>
              <p>Cookies are small text files stored on your device when you visit a website. They help websites function properly, provide security, and improve your experience.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. How We Use Cookies</h2>
              <p>We use cookies for the following purposes:</p>
              
              <h3 className="text-lg font-medium mt-4 mb-2">Essential Cookies (Required)</h3>
              <p>These cookies are necessary for the website to function and cannot be disabled.</p>
              <div className="bg-gray-50 p-4 rounded-md mt-2">
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 font-medium">auth_token</td>
                      <td className="py-2">Authentication session</td>
                      <td className="py-2">Session</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-medium">csrf_token</td>
                      <td className="py-2">Security protection</td>
                      <td className="py-2">Session</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-medium">cookie_consent</td>
                      <td className="py-2">Stores your cookie preferences</td>
                      <td className="py-2">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-medium mt-4 mb-2">Functional Cookies</h3>
              <p>These cookies enhance functionality and personalisation.</p>
              <div className="bg-gray-50 p-4 rounded-md mt-2">
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 font-medium">user_preferences</td>
                      <td className="py-2">Remembers your settings</td>
                      <td className="py-2">1 year</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-medium">language</td>
                      <td className="py-2">Language preference</td>
                      <td className="py-2">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-medium mt-4 mb-2">Analytics Cookies (Optional)</h3>
              <p>These cookies help us understand how visitors use our website.</p>
              <div className="bg-gray-50 p-4 rounded-md mt-2">
                <table className="w-full text-sm">
                  <tbody>
                    <tr>
                      <td className="py-2 font-medium">_ga, _gid</td>
                      <td className="py-2">Google Analytics</td>
                      <td className="py-2">2 years / 24 hours</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Third-Party Cookies</h2>
              <p>We use the following third-party services that may set cookies:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Stripe:</strong> Payment processing and fraud prevention</li>
                <li><strong>Google Analytics:</strong> Website usage analysis (if consent given)</li>
              </ul>
              <p className="mt-3">These third parties have their own privacy policies governing their use of cookies.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Managing Cookies</h2>
              <p>You can control cookies in several ways:</p>
              
              <h3 className="text-lg font-medium mt-4 mb-2">Cookie Consent Banner</h3>
              <p>When you first visit our site, you can choose which optional cookies to accept via our consent banner.</p>
              
              <h3 className="text-lg font-medium mt-4 mb-2">Browser Settings</h3>
              <p>Most browsers allow you to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>View existing cookies</li>
                <li>Delete cookies</li>
                <li>Block cookies from certain or all websites</li>
                <li>Block third-party cookies</li>
              </ul>
              
              <p className="mt-3">Note: Blocking essential cookies may prevent you from using our services.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Browser-Specific Instructions</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><a href="https://support.google.com/chrome/answer/95647" className="text-red-600" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" className="text-red-600" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" className="text-red-600" target="_blank" rel="noopener noreferrer">Safari</a></li>
                <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" className="text-red-600" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Updates to This Policy</h2>
              <p>We may update this Cookie Policy from time to time. We will notify you of any significant changes and update the "Last updated" date at the top of this page.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Contact Us</h2>
              <p>For questions about our use of cookies:</p>
              <p className="mt-2">
                <strong>Totalinfo Ltd</strong><br />
                Email: <a href="mailto:privacy@totalinfo.net" className="text-red-600">privacy@totalinfo.net</a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CookiePolicy;
